import { useEffect, useRef } from 'react';

const TURNSTILE_SITE_KEY = '0x4AAAAAACdNzZXJSykEthxk';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

// Skip Turnstile on localhost so dev still works without the challenge.
const skipTurnstile =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

interface TurnstileWidgetProps {
  /** Called with the token on success, or null on expiry/error. */
  onVerify: (token: string | null) => void;
  className?: string;
}

/**
 * Reusable Cloudflare Turnstile widget for public (unauthenticated) forms.
 *
 * Loads the Turnstile script once (guarded by id), renders an explicit widget
 * into a ref'd div, and reports the token via onVerify. On localhost it skips
 * the challenge entirely and immediately reports a constant 'localhost-skip'
 * token so dev flows continue to work.
 */
export function TurnstileWidget({ onVerify, className }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (skipTurnstile) {
      onVerify('localhost-skip');
      return;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled) return;
      const turnstile = (window as any).turnstile;
      if (!turnstile || !containerRef.current || widgetIdRef.current) return;

      widgetIdRef.current = turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => onVerify(token),
        'expired-callback': () => onVerify(null),
        'error-callback': () => onVerify(null),
        theme: 'auto',
      });
    };

    // If the API is already loaded, render immediately; otherwise load the
    // script once and render on its onload callback.
    if ((window as any).turnstile) {
      renderWidget();
    } else if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
      script.async = true;
      (window as any).onTurnstileLoad = () => renderWidget();
      document.head.appendChild(script);
    } else {
      // Script tag exists but API not ready yet — poll briefly for it.
      const interval = window.setInterval(() => {
        if ((window as any).turnstile) {
          window.clearInterval(interval);
          renderWidget();
        }
      }, 100);
      return () => {
        cancelled = true;
        window.clearInterval(interval);
      };
    }

    return () => {
      cancelled = true;
      const turnstile = (window as any).turnstile;
      if (turnstile && widgetIdRef.current && typeof turnstile.remove === 'function') {
        try {
          turnstile.remove(widgetIdRef.current);
        } catch {
          // Widget may already be gone — ignore.
        }
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render nothing on localhost (no challenge needed).
  if (skipTurnstile) return null;

  return <div ref={containerRef} className={className} />;
}

export default TurnstileWidget;
